import { defineStore } from 'pinia';
import type { Client, Subscription } from '@@/types/Clients';
import { useMetaStore } from './meta';

const APP_NAMES = [
  'media',
  'cms_v2',
  'rooms',
  'user_management',
  'app_manager',
  'engage',
  'inbox',
  'infolink',
  'navigate',
  'intranet',
  'community_engagement',
  'data_management',
  'analytics',
  'tasks',
  'attendance_pro',
  'smart_move',
] as const;

type SubscriptionKey =
  | 'mediaSubscription'
  | 'cmsV2Subscription'
  | 'roomsSubscription'
  | 'engageSubscription'
  | 'inboxSubscription'
  | 'appManagerSubscription'
  | 'userManagementSubscription'
  | 'infolinkSubscription'
  | 'navigateSubscription'
  | 'intranetSubscription'
  | 'communityEngagementSubscription'
  | 'dataManagementSubscription'
  | 'analyticsSubscription'
  | 'tasksSubscription'
  | 'attendanceProSubscription'
  | 'smartMoveSubscription';

type ClientSettingsExtended = Record<string, unknown> & {
  engage?: Record<string, unknown>;
  [key: string]: unknown;
};

interface ClientWithMeta extends Omit<Partial<Client>, 'settings'> {
  settings: ClientSettingsExtended;
  subscriptions?: Subscription[];
  meta?: {
    userStatuses?: unknown[];
    oauthApplications?: unknown[];
    recordCount?: number;
    fusionSyncEnabled?: boolean;
  };
}

export interface ClientState {
  client: ClientWithMeta;
  id: string;
  mediaSubscription: Subscription | null;
  cmsV2Subscription: Subscription | null;
  roomsSubscription: Subscription | null;
  engageSubscription: Subscription | null;
  inboxSubscription: Subscription | null;
  appManagerSubscription: Subscription | null;
  userManagementSubscription: Subscription | null;
  infolinkSubscription: Subscription | null;
  navigateSubscription: Subscription | null;
  intranetSubscription: Subscription | null;
  communityEngagementSubscription: Subscription | null;
  dataManagementSubscription: Subscription | null;
  analyticsSubscription: Subscription | null;
  tasksSubscription: Subscription | null;
  attendanceProSubscription: Subscription | null;
  smartMoveSubscription: Subscription | null;
}

const initialState = (): ClientState => ({
  client: { settings: {} },
  id: '',
  mediaSubscription: null,
  cmsV2Subscription: null,
  roomsSubscription: null,
  engageSubscription: null,
  inboxSubscription: null,
  appManagerSubscription: null,
  userManagementSubscription: null,
  infolinkSubscription: null,
  navigateSubscription: null,
  intranetSubscription: null,
  communityEngagementSubscription: null,
  dataManagementSubscription: null,
  analyticsSubscription: null,
  tasksSubscription: null,
  attendanceProSubscription: null,
  smartMoveSubscription: null,
});

const normalizeAppName = (name: string) => (name || '').replace(/\s+/g, '_').toLowerCase();

function snakeToCamelSubscriptionKey(app: string): SubscriptionKey {
  const camel = app.replace(/_(.)/g, (_, c: string) => c.toUpperCase());
  return `${camel}Subscription` as SubscriptionKey;
}

export const useClientStore = defineStore('client', {
  state: initialState,
  getters: {
    getClientId: (state) => state.id,
    isClientAdmin: (state) => parseInt(state.id) === 1,
    getOAuthIdByName: (state) => (applicationName: string) => {
      const key = `${applicationName}Subscription` as SubscriptionKey;
      return state[key]?.oauthApplicationId;
    },
  },
  actions: {
    resetClientState() {
      Object.assign(this, initialState());
    },
    parseClientSettings() {
      if (typeof this.client.settings === 'string') {
        this.client.settings = JSON.parse(this.client.settings as unknown as string);
      }
    },
    setClientDetails(client: ClientWithMeta) {
      this.client = client;
      this.id = (client.id ?? '') as string;
    },
    setSubscriptions() {
      const clientSubscriptions = this.client.subscriptions ?? [];
      for (const app of APP_NAMES) {
        const subscription = clientSubscriptions.find(
          (sub: Subscription) => normalizeAppName(sub.applicationName) === app,
        );
        const key = snakeToCamelSubscriptionKey(app);
        this[key] = subscription ?? null;
      }
    },
    async fetchClientInfo(id: string | number) {
      const { clients } = await useApi();
      const client = await clients.find(id);
      this.resetClientState();
      this.setClientDetails(client as ClientWithMeta);
      this.setSubscriptions();
      this.parseClientSettings();
      useMetaStore().setMeta(client.meta);
    },
    updateClient({ property, value }: { property: string; value: unknown }) {
      const clientCopy = JSON.parse(JSON.stringify(this.client));
      clientCopy[property] = value;
      this.setClientDetails(clientCopy);
    },
    updateClientSettings({ property, value }: { property: string; value: unknown }) {
      const clientCopy = JSON.parse(JSON.stringify(this.client));
      if (clientCopy.settings === undefined) {
        return;
      }
      clientCopy.settings[property] = value;
      this.setClientDetails(clientCopy);
    },
  },
});
